import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api, { formatDate } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Shield, Building2, Users, Settings, Activity, Plus, Search, RefreshCw,
  Key, UserCheck, UserX, Trash2, Edit, AlertTriangle, ExternalLink,
  ClipboardList, FileText, Package, BarChart3, MessageCircle, Mail,
  RotateCcw, Database, HardDrive, Loader2, CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

const SUPER_ADMIN_EMAIL = 'ahmad.disi@unknownroom.com';

export default function SuperAdminConsolePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="w-96 bg-card border-border">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">{t('audit.accessDenied')}</h2>
            <p className="text-muted-foreground">{t('audit.superAdminOnly')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="super-admin-console" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-3xl text-foreground tracking-tight flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-500" />
            {t('superAdmin.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{t('superAdmin.subtitle')}</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
            <BarChart3 className="w-4 h-4" /> {t('superAdmin.overview')}
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
            <Users className="w-4 h-4" /> {t('superAdmin.usersAccess')}
          </TabsTrigger>
          <TabsTrigger value="config" className="flex items-center gap-2" data-testid="tab-config">
            <Settings className="w-4 h-4" /> {t('superAdmin.configuration')}
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2" data-testid="tab-audit">
            <Activity className="w-4 h-4" /> {t('superAdmin.auditTools')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="config"><ConfigTab /></TabsContent>
        <TabsContent value="audit"><AuditToolsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ======================== OVERVIEW TAB ========================
function OverviewTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/super/stats').then(({ data }) => { setStats(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-muted-foreground">{t('common.loading')}</p>;
  if (!stats) return null;

  const kpis = [
    { label: t('superAdmin.branches'), value: stats.branches, icon: Building2, color: 'text-blue-500 bg-blue-500/10' },
    { label: t('superAdmin.activeUsers'), value: `${stats.users_active}/${stats.users_total}`, icon: Users, color: 'text-green-500 bg-green-500/10' },
    { label: t('superAdmin.openWOs'), value: stats.open_wos, icon: ClipboardList, color: 'text-brand bg-brand/10' },
    { label: t('superAdmin.overdueWOs'), value: stats.overdue_wos, icon: AlertTriangle, color: 'text-red-500 bg-red-500/10' },
    { label: t('superAdmin.openWRs'), value: stats.open_wrs, icon: FileText, color: 'text-purple-500 bg-purple-500/10' },
    { label: t('superAdmin.lowStock'), value: stats.low_stock, icon: Package, color: 'text-amber-500 bg-amber-500/10' },
    { label: t('superAdmin.emailsSent'), value: stats.emails_sent, icon: Mail, color: 'text-teal-500 bg-teal-500/10' },
    { label: t('superAdmin.chatMessages'), value: stats.chat_messages, icon: MessageCircle, color: 'text-indigo-500 bg-indigo-500/10' },
  ];

  const quickActions = [
    { label: t('superAdmin.addBranch'), path: '/branches', icon: Building2 },
    { label: t('superAdmin.addUser'), path: '/users', icon: Users },
    { label: t('superAdmin.importCodes'), path: '/import-codes', icon: Package },
    { label: t('superAdmin.dataChecks'), path: '/data-checks', icon: AlertTriangle },
    { label: t('superAdmin.systemStatus'), path: '/system-status', icon: Activity },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <Card key={i} className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${kpi.color}`}>
                <kpi.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{kpi.label}</p>
                <p className="text-xl font-bold text-foreground">{kpi.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-barlow">{t('superAdmin.quickActions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {quickActions.map((action, i) => (
              <Button key={i} variant="outline" onClick={() => navigate(action.path)} className="flex items-center gap-2">
                <action.icon className="w-4 h-4" />
                {action.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase mb-1">{t('superAdmin.pmPlans')}</p>
            <p className="text-lg font-bold text-foreground">{stats.pm_active_plans} active</p>
            <p className="text-xs text-muted-foreground">{stats.pm_overdue_runs} overdue runs</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase mb-1">{t('superAdmin.slaBreaches')}</p>
            <p className="text-lg font-bold text-foreground">{stats.sla_breaches_today} today</p>
            <p className="text-xs text-muted-foreground">{stats.closed_wos} WOs closed total</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ======================== USERS TAB ========================
function UsersTab() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [branches, setBranches] = useState([]);

  // Dialog states
  const [showCreate, setShowCreate] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [showEdit, setShowEdit] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const [usersRes, branchRes] = await Promise.all([
        api.get('/admin/super/users'),
        api.get('/branches')
      ]);
      setUsers(usersRes.data);
      setBranches(branchRes.data);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filteredUsers = users.filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await api.post(`/admin/super/users/${userId}/toggle-active`, { is_active: !currentActive });
      toast.success(currentActive ? t('superAdmin.userDeactivated') : t('superAdmin.userActivated'));
      loadUsers();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const handleDeleteUser = async (targetUser) => {
    if (!window.confirm(t('superAdmin.confirmDeleteUser', { name: targetUser.name }))) return;
    try {
      await api.delete(`/users/${targetUser.id}`);
      toast.success(t('superAdmin.userDeleted', { name: targetUser.name }));
      loadUsers();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const roleBadge = (role) => {
    const colors = {
      admin: 'bg-red-500/10 text-red-500',
      supervisor: 'bg-purple-500/10 text-purple-500',
      technician: 'bg-blue-500/10 text-blue-500',
      requester: 'bg-green-500/10 text-green-500'
    };
    return <Badge className={colors[role] || ''}>{role}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('superAdmin.searchUsers')} className="ps-9" data-testid="user-search" />
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-user-btn">
          <Plus className="w-4 h-4 me-2" /> {t('superAdmin.createUser')}
        </Button>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs font-semibold uppercase">{t('superAdmin.name')}</TableHead>
                <TableHead className="text-xs font-semibold uppercase">{t('superAdmin.email')}</TableHead>
                <TableHead className="text-xs font-semibold uppercase">{t('superAdmin.username')}</TableHead>
                <TableHead className="text-xs font-semibold uppercase">{t('superAdmin.role')}</TableHead>
                <TableHead className="text-xs font-semibold uppercase">{t('superAdmin.branchAccess')}</TableHead>
                <TableHead className="text-xs font-semibold uppercase">{t('common.status')}</TableHead>
                <TableHead className="text-xs font-semibold uppercase">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(u => (
                <TableRow key={u.id} className={!u.is_active ? 'opacity-50' : ''}>
                  <TableCell className="font-medium">
                    {u.name}
                    {u.is_super_admin && <Badge className="ms-2 bg-red-500/10 text-red-500 text-[10px]">Super Admin</Badge>}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{u.username || '—'}</TableCell>
                  <TableCell>{roleBadge(u.role)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{u.branch_names?.join(', ') || '—'}</TableCell>
                  <TableCell>
                    {u.is_active ? (
                      <Badge className="bg-green-500/10 text-green-500">{t('common.active')}</Badge>
                    ) : (
                      <Badge className="bg-red-500/10 text-red-500">{t('common.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setShowEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setShowResetPw(u)} data-testid={`reset-pw-${u.id}`}>
                        <Key className="w-3.5 h-3.5" />
                      </Button>
                      {!u.is_super_admin && (
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u.id, u.is_active)}
                          className={u.is_active ? 'text-red-500' : 'text-green-500'} data-testid={`toggle-active-${u.id}`}>
                          {u.is_active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                      {!u.is_super_admin && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u)} className="text-red-500" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CreateUserDialog open={showCreate} onOpenChange={setShowCreate} branches={branches} onCreated={loadUsers} />
      <ResetPasswordDialog user={showResetPw} onClose={() => setShowResetPw(null)} />
      <EditUserDialog user={showEdit} onClose={() => setShowEdit(null)} branches={branches} onSaved={loadUsers} />
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, branches, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', username: '', password: '', role: 'requester', allowed_branch_ids: [] });
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) { toast.error(t('superAdmin.fillRequired')); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.username) delete payload.username;
      await api.post('/users', payload);
      toast.success(t('superAdmin.userCreated'));
      setForm({ name: '', email: '', username: '', password: '', role: 'requester', allowed_branch_ids: [] });
      onOpenChange(false);
      onCreated();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const toggleBranch = (bid) => {
    setForm(prev => ({
      ...prev,
      allowed_branch_ids: prev.allowed_branch_ids.includes(bid)
        ? prev.allowed_branch_ids.filter(id => id !== bid)
        : [...prev.allowed_branch_ids, bid]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader><DialogTitle className="font-barlow">{t('superAdmin.createUser')}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('superAdmin.name')} *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="new-user-name" /></div>
            <div><Label>{t('superAdmin.email')} *</Label><Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="new-user-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('superAdmin.username')} <span className="text-muted-foreground text-xs">({t('common.optional')})</span></Label>
              <Input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} placeholder="e.g. DC90" data-testid="new-user-username" />
            </div>
            <div>
              <Label>{t('superAdmin.oneTimePassword')} *</Label>
              <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} data-testid="new-user-password" />
              <p className="text-xs text-muted-foreground mt-1">{t('superAdmin.oneTimePasswordHint')}</p>
            </div>
          </div>
          <div>
            <Label>{t('superAdmin.role')}</Label>
            <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="requester">Requester</SelectItem>
                <SelectItem value="technician">Technician</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('superAdmin.branchAccess')}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {branches.map(b => (
                <Badge key={b.id} variant="outline" onClick={() => toggleBranch(b.id)}
                  className={`cursor-pointer ${form.allowed_branch_ids.includes(b.id) ? 'bg-brand/10 border-brand text-brand' : ''}`}>
                  {b.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate} disabled={loading} data-testid="confirm-create-user">{loading ? t('common.loading') : t('common.create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user: targetUser, onClose }) {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (!targetUser) return null;

  const handleReset = async () => {
    if (newPassword.length < 4) { toast.error(t('superAdmin.passwordTooShort')); return; }
    setLoading(true);
    try {
      await api.post(`/admin/super/users/${targetUser.id}/reset-password`, { new_password: newPassword });
      toast.success(t('superAdmin.passwordReset', { name: targetUser.name }));
      setNewPassword('');
      onClose();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!targetUser} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-barlow">{t('superAdmin.resetPassword')}</DialogTitle>
          <DialogDescription>{targetUser.name} ({targetUser.email})</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <Label>{t('superAdmin.oneTimePassword')}</Label>
          <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 4 characters" data-testid="reset-pw-input" />
          <p className="text-xs text-muted-foreground">{t('superAdmin.resetPasswordHint')}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleReset} disabled={loading || newPassword.length < 4} data-testid="confirm-reset-pw">{t('superAdmin.resetPassword')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user: targetUser, onClose, branches, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (targetUser) setForm({ name: targetUser.name, email: targetUser.email, role: targetUser.role, allowed_branch_ids: targetUser.allowed_branch_ids || [] });
  }, [targetUser]);

  if (!targetUser) return null;

  const toggleBranch = (bid) => {
    setForm(prev => ({
      ...prev,
      allowed_branch_ids: prev.allowed_branch_ids.includes(bid)
        ? prev.allowed_branch_ids.filter(id => id !== bid)
        : [...prev.allowed_branch_ids, bid]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(`/users/${targetUser.id}`, form);
      toast.success(t('toast.saved'));
      onClose();
      onSaved();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!targetUser} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader><DialogTitle className="font-barlow">{t('superAdmin.editUser')}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('superAdmin.name')}</Label><Input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><Label>{t('superAdmin.email')}</Label><Input value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
          </div>
          <div>
            <Label>{t('superAdmin.role')}</Label>
            <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="requester">Requester</SelectItem>
                <SelectItem value="technician">Technician</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('superAdmin.branchAccess')}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {branches.map(b => (
                <Badge key={b.id} variant="outline" onClick={() => toggleBranch(b.id)}
                  className={`cursor-pointer ${form.allowed_branch_ids?.includes(b.id) ? 'bg-brand/10 border-brand text-brand' : ''}`}>
                  {b.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} disabled={loading}>{t('common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ======================== CONFIG TAB ========================
function ConfigTab() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(null); // category name
  const [newItem, setNewItem] = useState({ value: '', label_en: '', label_ar: '' });

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/super/config');
      setConfig(data);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleAdd = async (category) => {
    if (!newItem.value || !newItem.label_en) { toast.error(t('superAdmin.fillRequired')); return; }
    try {
      await api.post('/admin/super/config', { category, ...newItem });
      toast.success(t('superAdmin.configAdded'));
      setNewItem({ value: '', label_en: '', label_ar: '' });
      setShowAdd(null);
      loadConfig();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm(t('superAdmin.confirmDeleteConfig'))) return;
    try {
      await api.delete(`/admin/super/config/${itemId}`);
      toast.success(t('common.delete'));
      loadConfig();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const categoryNames = {
    asset_categories: t('superAdmin.assetCategories'),
    failure_categories: t('superAdmin.failureCategories')
  };

  if (loading) return <p className="text-muted-foreground">{t('common.loading')}</p>;

  return (
    <div className="space-y-6">
      {Object.entries(config).map(([category, items]) => (
        <Card key={category} className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-barlow">{categoryNames[category] || category}</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowAdd(showAdd === category ? null : category)} data-testid={`add-${category}`}>
                <Plus className="w-4 h-4 me-2" /> {t('superAdmin.addItem')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {showAdd === category && (
              <div className="flex gap-2 mb-4 p-3 bg-muted/30 rounded-lg">
                <Input placeholder="Value (key)" value={newItem.value} onChange={e => setNewItem(p => ({ ...p, value: e.target.value }))} className="flex-1" data-testid={`new-${category}-value`} />
                <Input placeholder="English label" value={newItem.label_en} onChange={e => setNewItem(p => ({ ...p, label_en: e.target.value }))} className="flex-1" />
                <Input placeholder="Arabic label" value={newItem.label_ar} onChange={e => setNewItem(p => ({ ...p, label_ar: e.target.value }))} className="flex-1" />
                <Button size="sm" onClick={() => handleAdd(category)} data-testid={`confirm-add-${category}`}>{t('common.save')}</Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {items.map(item => (
                <Badge key={item.id} variant="outline" className="px-3 py-1.5 flex items-center gap-2">
                  <span>{isAr ? item.label_ar : item.label_en}</span>
                  <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ======================== AUDIT TOOLS TAB ========================
function AuditToolsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showFactoryReset, setShowFactoryReset] = useState(false);

  const links = [
    { label: t('superAdmin.auditPanel'), path: '/super-admin-audit', icon: Shield, desc: t('superAdmin.auditPanelDesc') },
    { label: t('superAdmin.systemStatus'), path: '/system-status', icon: Activity, desc: t('superAdmin.systemStatusDesc') },
    { label: t('superAdmin.dataChecks'), path: '/data-checks', icon: AlertTriangle, desc: t('superAdmin.dataChecksDesc') },
    { label: t('superAdmin.importCodes'), path: '/import-codes', icon: Package, desc: t('superAdmin.importCodesDesc') },
  ];

  return (
    <div className="space-y-6">
      {/* Factory Reset Card */}
      <Card className="bg-red-500/5 border-red-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-barlow flex items-center gap-2 text-red-500">
            <RotateCcw className="w-5 h-5" />
            {t('superAdmin.factoryReset', 'Factory Reset / Start Fresh')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {t('superAdmin.factoryResetDesc', 'Delete all data and start with a clean system. Only the Super Admin account and system categories will be preserved.')}
          </p>
          <Button 
            variant="destructive" 
            onClick={() => setShowFactoryReset(true)}
            data-testid="factory-reset-btn"
          >
            <Trash2 className="w-4 h-4 me-2" />
            {t('superAdmin.openFactoryReset', 'Open Factory Reset Tool')}
          </Button>
        </CardContent>
      </Card>

      {/* Existing links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {links.map((link, i) => (
          <Card key={i} className="bg-card border-border cursor-pointer hover:border-brand/30 transition-colors" onClick={() => navigate(link.path)}>
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                <link.icon className="w-5 h-5 text-brand" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground flex items-center gap-2">
                  {link.label} <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </p>
                <p className="text-sm text-muted-foreground mt-1">{link.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Factory Reset Dialog */}
      <FactoryResetDialog open={showFactoryReset} onOpenChange={setShowFactoryReset} />
    </div>
  );
}

// ======================== FACTORY RESET DIALOG ========================
function FactoryResetDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('preview'); // preview, confirm1, confirm2, executing, complete
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [confirmAppName, setConfirmAppName] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Load preview when dialog opens
  useEffect(() => {
    if (open) {
      setStep('preview');
      setConfirmDelete('');
      setConfirmAppName('');
      setResult(null);
      setError(null);
      loadPreview();
    }
  }, [open]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/super/factory-reset/preview');
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (confirmDelete !== 'DELETE') {
      toast.error('Please type DELETE exactly');
      return;
    }
    if (confirmAppName !== 'UR Maintenance') {
      toast.error('Please type UR Maintenance exactly');
      return;
    }

    setStep('executing');
    setLoading(true);
    try {
      const { data } = await api.post('/admin/super/factory-reset/execute', {
        confirm_delete: confirmDelete,
        confirm_app_name: confirmAppName
      });
      setResult(data);
      setStep('complete');
      toast.success('Factory reset completed successfully');
    } catch (err) {
      setError(getErrorMessage(err));
      setStep('complete');
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (step === 'executing') return; // Prevent closing during execution
    onOpenChange(false);
    if (step === 'complete' && result?.success) {
      // Reload the page to reflect changes
      window.location.reload();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-barlow text-red-500 flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            {t('superAdmin.factoryReset', 'Factory Reset / Start Fresh')}
          </DialogTitle>
          <DialogDescription>
            {t('superAdmin.factoryResetWarning', 'This action will permanently delete all data. This cannot be undone.')}
          </DialogDescription>
        </DialogHeader>

        {/* Step: Preview */}
        {step === 'preview' && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-brand" />
              </div>
            ) : preview ? (
              <>
                {/* Warning Banner */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-500">{t('superAdmin.dangerZone', 'Danger Zone')}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('superAdmin.factoryResetDangerText', 'You are about to delete ALL data from the system. This includes work orders, work requests, users, branches, assets, rooms, inventory, and all files stored in R2.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preserved Items */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-500">{t('superAdmin.preserved', 'Will Be Preserved')}</p>
                      <ul className="text-sm text-muted-foreground mt-1 list-disc list-inside">
                        <li>Super Admin: {preview.preserved.super_admin.email} ({preview.preserved.super_admin.username})</li>
                        <li>System Config (asset/failure categories)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Data to be deleted */}
                <div>
                  <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    {t('superAdmin.dataToDelete', 'Data to be Deleted')} ({preview.total_documents.toLocaleString()} documents)
                  </h4>
                  <div className="bg-muted/30 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {preview.collections.filter(c => c.count > 0).map((col, i) => (
                        <div key={i} className="flex justify-between">
                          <span className="text-muted-foreground">{col.display_name}</span>
                          <span className="font-mono text-foreground">{col.count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* R2 Storage */}
                {preview.r2_objects !== 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <HardDrive className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t('superAdmin.r2Objects', 'R2 Storage Objects')}:</span>
                    <span className="font-mono text-foreground">{String(preview.r2_objects).toLocaleString()}</span>
                  </div>
                )}
              </>
            ) : error ? (
              <div className="text-red-500 text-center py-4">{error}</div>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>{t('common.cancel')}</Button>
              <Button 
                variant="destructive" 
                onClick={() => setStep('confirm1')} 
                disabled={loading || !preview}
                data-testid="proceed-to-confirm"
              >
                {t('superAdmin.proceedToConfirm', 'Proceed to Confirmation')}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Confirm 1 */}
        {step === 'confirm1' && (
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-red-500">{t('superAdmin.confirmStep1', 'Confirmation Step 1 of 2')}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {t('superAdmin.confirmStep1Text', 'To confirm you want to delete all data, type DELETE below:')}
              </p>
            </div>

            <div>
              <Label className="text-red-500">{t('superAdmin.typeDelete', 'Type DELETE to confirm')}</Label>
              <Input 
                value={confirmDelete}
                onChange={e => setConfirmDelete(e.target.value)}
                placeholder="DELETE"
                className="mt-2 font-mono text-center text-lg"
                data-testid="confirm-delete-input"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('preview')}>{t('common.back')}</Button>
              <Button 
                variant="destructive" 
                onClick={() => setStep('confirm2')} 
                disabled={confirmDelete !== 'DELETE'}
                data-testid="proceed-to-confirm2"
              >
                {t('common.next')}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Confirm 2 */}
        {step === 'confirm2' && (
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-red-500">{t('superAdmin.confirmStep2', 'Confirmation Step 2 of 2')}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {t('superAdmin.confirmStep2Text', 'Final confirmation: type the application name to proceed:')}
              </p>
            </div>

            <div>
              <Label className="text-red-500">{t('superAdmin.typeAppName', 'Type UR Maintenance to confirm')}</Label>
              <Input 
                value={confirmAppName}
                onChange={e => setConfirmAppName(e.target.value)}
                placeholder="UR Maintenance"
                className="mt-2 font-mono text-center text-lg"
                data-testid="confirm-app-name-input"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('confirm1')}>{t('common.back')}</Button>
              <Button 
                variant="destructive" 
                onClick={handleExecute} 
                disabled={confirmAppName !== 'UR Maintenance'}
                data-testid="execute-factory-reset"
              >
                <Trash2 className="w-4 h-4 me-2" />
                {t('superAdmin.executeReset', 'Execute Factory Reset')}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Executing */}
        {step === 'executing' && (
          <div className="py-12 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-red-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">{t('superAdmin.resetting', 'Resetting System...')}</p>
            <p className="text-sm text-muted-foreground mt-2">{t('superAdmin.pleaseWait', 'Please wait, this may take a moment.')}</p>
          </div>
        )}

        {/* Step: Complete */}
        {step === 'complete' && (
          <div className="space-y-4">
            {result?.success ? (
              <>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-medium text-green-500">{t('superAdmin.resetComplete', 'Factory Reset Complete')}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('superAdmin.resetCompleteText', 'The system has been reset. All data has been deleted.')}
                  </p>
                </div>

                {/* Results summary */}
                <div className="bg-muted/30 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <h4 className="font-medium text-foreground mb-2">{t('superAdmin.deletionSummary', 'Deletion Summary')}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {result.collections_deleted?.filter(c => c.deleted > 0).map((col, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-muted-foreground">{col.display_name}</span>
                        <span className="font-mono text-green-500">-{col.deleted.toLocaleString()}</span>
                      </div>
                    ))}
                    {result.r2_result?.deleted > 0 && (
                      <div className="flex justify-between col-span-2 border-t pt-2 mt-2">
                        <span className="text-muted-foreground">R2 Objects</span>
                        <span className="font-mono text-green-500">-{result.r2_result.deleted.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <p className="text-lg font-medium text-red-500">{t('superAdmin.resetFailed', 'Factory Reset Failed')}</p>
                <p className="text-sm text-muted-foreground mt-2">{error || 'An error occurred during the reset.'}</p>
              </div>
            )}

            <DialogFooter>
              <Button onClick={handleClose} data-testid="close-factory-reset">
                {result?.success ? t('superAdmin.reloadApp', 'Reload Application') : t('common.close')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
